import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { OperationType, Token, TokenStandardType } from '@archax/shared-types'
import { useMutation } from '@tanstack/react-query'
import { Box, Button, ButtonGroup } from '@mui/material'
import { isValidAddress } from '@archax/address-validator'
import { yupResolver } from '@hookform/resolvers/yup'
import { createOperation } from '../../../../api/operations'
import onApiError from '../../../../util/on-api-error'
import WalletAddressInput from '../../../../components/ui/WalletAddressInput'
import { StyledForm } from './styled-components'
import { StyledCircularProgress } from '../../../../components/ui/styled-components'
import { tokenAmountValidator } from '../../../../util/yup-validators'
import { TextField } from '../../../../components/ui/TextField'
import { Select } from '../../../../components/ui/Select'

type TransferOutFormData = {
  tokenType: string
  tokenAddress: string
  recipientAddress: string
  amount: number
}

type TransferOutFormProps = {
  token: Token
  onCancel: () => void
  onSuccess: () => void
}
export function TransferOutForm({ token, onCancel, onSuccess }: TransferOutFormProps) {
  const {
    standard: {
      chain: { protocol },
    },
  } = token

  const { mutateAsync, isLoading } = useMutation(
    (operationData: Record<string, unknown>) => {
      return createOperation(OperationType.TransferOut, operationData, token.id)
    },
    {
      onSuccess: () => {
        toast.success('Transfer out request sent for approval')
        onSuccess()
      },
      onError: onApiError,
    },
  )

  const initialValues = {
    tokenType: TokenStandardType.ERC20,
    tokenAddress: '',
    recipientAddress: '',
    amount: '0',
  } as any
  const validationSchema = Yup.object()
    .shape({
      tokenType: Yup.string().oneOf(Object.values(TokenStandardType)).required(),
      tokenAddress: Yup.string()
        .test('is-valid-address', 'Invalid address', (value: string | undefined, ctx: Yup.TestContext): boolean => {
          if (!value) {
            return false
          }
          return isValidAddress(value, protocol)
        })
        .required(),
      recipientAddress: Yup.string()
        .test('is-valid-address', 'Invalid address', (value: string | undefined, ctx: Yup.TestContext): boolean => {
          if (!value) {
            return false
          }
          return isValidAddress(value, protocol)
        })
        .required(),
      amount: tokenAmountValidator({ decimals: token.decimals, positive: true }),
    })
    .required()

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<TransferOutFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  })

  const submitForm = async (formData: TransferOutFormData) => {
    if (!isSubmitting) {
      return onSubmit({
        tokenType: formData.tokenType,
        tokenAddress: formData.tokenAddress,
        recipientAddress: formData.recipientAddress,
        amount: formData.amount,
      })
    }
  }

  const onSubmit = async (operationData: Record<string, unknown>) => {
    if (!isLoading) {
      return mutateAsync(operationData)
    }
  }

  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <Select
        label="Token type"
        name="tokenType"
        control={control}
        options={[{ label: 'ERC20', value: TokenStandardType.ERC20 }]}
      />
      <WalletAddressInput
        control={control as any}
        label="Token address"
        name="tokenAddress"
        protocol={protocol}
        privacyEnabled={false}
      />
      <WalletAddressInput
        control={control as any}
        label="Recipient address"
        name="recipientAddress"
        protocol={protocol}
        privacyEnabled={false}
      />
      <TextField name="amount" type="number" control={control} label="Amount" />
      <Box m={1} />
      <Box m={4} />
      <ButtonGroup fullWidth>
        {onCancel && (
          <Button size="large" fullWidth onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="contained" size="large" color="primary" fullWidth disabled={!isValid}>
          {isSubmitting && <StyledCircularProgress size={'1.2rem'} />}
          {isSubmitting ? 'Submitting' : 'Submit'}
        </Button>
      </ButtonGroup>
    </StyledForm>
  )
}
